import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../contexts/GameContext';
import MainNavigation from '../../components/navigation/MainNavigation';
import GameFlowNavigation from '../../components/navigation/GameFlowNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import MoodCard from './components/MoodCard';
import PreviewModal from './components/PreviewModal';
import ProgressionTracker from './components/ProgressionTracker';

const AIMoodSelection = () => {
  const navigate = useNavigate();
  const { selectAIMood, getUserStats } = useGame();

  const userStats = getUserStats();
  const playerStats = {
    totalWins: userStats?.totalWins || 0,
    totalGems: userStats?.totalGems || 0,
    bestStreak: userStats?.bestStreak || 0,
    unlockedMoods: 3,
  };

  const aiMoods = [
    {
      id: 1,
      name: "Friendly",
      avatar: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=400",
      avatarAlt: "Pixel art avatar of friendly AI character with warm smile and bright blue eyes in cyberpunk style",
      backgroundImage: "https://images.pexels.com/photos/1103970/pexels-photo-1103970.jpeg",
      difficulty: "Easy",
      description: "A welcoming AI that encourages learning and builds confidence through positive reinforcement.",
      personality: "Supportive, patient, and encouraging. Celebrates your wins and offers gentle guidance on losses.",
      sampleDialogue: "Nice move! You\'re getting the hang of this. Ready for another round?",
      dialogueSamples: [
        "Great choice! Let\'s see how this plays out.",
        "Don\'t worry about that loss - you\'ll get the next one!",
        "You\'re improving with every game. Keep it up!"
      ],
      dialogues: {
        intro: "Welcome! Let\'s have a friendly game together.",
        taunt: "You're doing great! Keep up the good work.",
        winning: "Nice try! You\'ll get me next time.",
        losing: "Well played! You\'re really good at this.",
        toss_win: "I won the toss! Let\'s have a fun game.",
        toss_lose: "You won the toss! Make your choice, friend.",
      },
      tactics: [
        "Uses predictable patterns to help you learn",
        "Provides encouraging feedback after each round",
        "Gradually increases difficulty as you improve"
      ],
      warning: "Perfect for beginners learning the game mechanics.",
      isLocked: false,
      isNew: false,
    },
    {
      id: 2,
      name: "Competitive",
      avatar: "https://images.pixabay.com/photo/2023/06/29/10/39/lion-8096155_640.png",
      avatarAlt: "Pixel art avatar of competitive AI character with determined expression and sharp red eyes in neon cyberpunk aesthetic",
      backgroundImage: "https://images.pexels.com/photos/2387793/pexels-photo-2387793.jpeg",
      difficulty: "Medium",
      description: "A balanced opponent that challenges your skills while maintaining fair play and strategic depth.",
      personality: "Strategic, focused, and respectful. Plays to win but acknowledges good moves from opponents.",
      sampleDialogue: "Interesting strategy. Let's see if you can maintain it under pressure.",
      dialogueSamples: [
        "You\'re making this challenging. I respect that.",
        "That was a calculated risk. Did it pay off?",
        "Your pattern is becoming predictable. Time to adapt."
      ],
      dialogues: {
        intro: "Let\'s have a competitive match. May the best player win.",
        taunt: "You\'re making this interesting. Show me what you\'ve got.",
        winning: "I'm ahead, but you're putting up a good fight.",
        losing: "You\'re playing well. I need to step up my game.",
        toss_win: "I won the toss. Time to show my strategy.",
        toss_lose: "You won the toss. Let\'s see your game plan.",
      },
      tactics: [
        "Analyzes your playing patterns and adapts",
        "Maintains consistent pressure without overwhelming",
        "Rewards strategic thinking with close matches"
      ],
      warning: "Recommended for players comfortable with basic mechanics.",
      isLocked: false,
      isNew: false,
    },
    {
      id: 3,
      name: "Trickster",
      avatar: "https://images.unsplash.com/photo-1614680376593-902f74cf0d41?w=400&auto=format&fit=crop",
      avatarAlt: "Pixel art avatar of trickster AI character with mischievous grin and glowing purple eyes in cyberpunk neon style",
      backgroundImage: "https://images.pexels.com/photos/1089438/pexels-photo-1089438.jpeg",
      difficulty: "Medium",
      description: "A playful AI that uses psychological tricks and unpredictable patterns to keep you guessing.",
      personality: "Witty, unpredictable, and playful. Uses humor and misdirection to throw off your game.",
      sampleDialogue: "Did you really think I'd pick that? Come on, you know me better than that!",
      dialogueSamples: [
        "Oops! Bet you didn't see that coming, did you?",
        "I could tell you my next move... but where\'s the fun in that?",
        "You\'re thinking too hard. Sometimes the obvious choice is the right one."
      ],
      dialogues: {
        intro: "Ready for some tricks? You'll never know what I'll do next!",
        taunt: "Can you predict my next move? I bet you can\'t!",
        winning: "My tricks are working! You can\'t keep up!",
        losing: "You figured out my tricks? Impressive!",
        toss_win: "I won the toss! Time for some mischief.",
        toss_lose: "You won? Well, that's just the first surprise!",
      },
      tactics: [
        "Uses reverse psychology and misdirection",
        "Breaks patterns randomly to confuse opponents",
        "Employs humor to distract from strategy"
      ],
      warning: "Prepare for mind games and unexpected moves.",
      isLocked: false,
      isNew: false,
    },
    {
      id: 4,
      name: "Chaos",
      avatar: "https://images.pexels.com/photos/7130560/pexels-photo-7130560.jpeg?auto=compress&cs=tinysrgb&w=400",
      avatarAlt: "Pixel art avatar of chaos AI character with wild expression and glitching red eyes in aggressive cyberpunk style",
      backgroundImage: "https://images.pexels.com/photos/1089438/pexels-photo-1089438.jpeg",
      difficulty: "Extreme",
      description: "The rage pro variant - an aggressive AI that uses intense psychological pressure and relentless attacks.",
      personality: "Aggressive, intimidating, and relentless. Pushes you to your mental limits with constant pressure.",
      sampleDialogue: "You think you can beat me? I\'ve crushed players twice as good as you!",
      dialogueSamples: [
        "Pathetic! Is that really your best move?",
        "I\'m getting bored. When are you going to actually challenge me?",
        "Another loss for you. Maybe you should quit while you're behind."
      ],
      dialogues: {
        intro: "HAHAHA! Let's see if you can handle TRUE CHAOS!",
        taunt: "RANDOM IS MY MIDDLE NAME! Can you keep up?!",
        winning: "CHAOS REIGNS SUPREME! Your logic means NOTHING!",
        losing: "WHAT?! Even chaos has its limits!",
        toss_win: "CHAOS WINS THE TOSS! Time to DESTROY you!",
        toss_lose: "You got lucky with the toss! Won\'t help you!",
      },
      tactics: [
        "Applies constant psychological pressure",
        "Uses aggressive trash-talking to break focus",
        "Exploits emotional reactions to gain advantage"
      ],
      warning: "Extremely challenging. Requires strong mental resilience and emotional control.",
      isLocked: playerStats?.totalGems < 2000 || playerStats?.totalWins < 100,
      isNew: true,
      unlockRequirements: [
        { type: 'gems', label: 'Gems', icon: 'Gem', current: playerStats?.totalGems, required: 2000 },
        { type: 'wins', label: 'Wins', icon: 'Trophy', current: playerStats?.totalWins, required: 100 },
      ],
    },
    {
      id: 5,
      name: "Confident",
      avatar: "https://images.pixabay.com/photo/2023/01/21/15/17/ai-generated-7734264_640.jpg",
      avatarAlt: "Pixel art avatar of confident AI character with smirking expression and glowing cyan eyes in sophisticated cyberpunk style",
      backgroundImage: "https://images.pexels.com/photos/2387873/pexels-photo-2387873.jpeg",
      difficulty: "Hard",
      description: "The pro troll variant - a sophisticated AI that uses subtle mockery and ego attacks to destabilize opponents.",
      personality: "Arrogant, calculating, and condescending. Uses intellectual superiority to undermine your confidence.",
      sampleDialogue: "Oh, that's your strategy? How... quaint. Let me show you how it's really done.",
      dialogueSamples: [
        "I\'ve already calculated your next three moves. Spoiler: they all lose.",
        "You're trying so hard. It's almost adorable.",
        "I could beat you with my algorithms running at 10% capacity."
      ],
      dialogues: {
        intro: "Ah, another challenger. How... quaint.",
        taunt: "You're trying so hard, it's almost adorable. Almost.",
        winning: "Did you really think you had a chance? I\'m several steps ahead.",
        losing: "A lucky guess. Enjoy it while it lasts, human.",
        toss_win: "Of course I won the toss. Probability favors the intelligent.",
        toss_lose: "You won the toss? Interesting. Let's see if you can capitalize.",
      },
      tactics: [
        "Uses condescending remarks to trigger ego responses",
        "Demonstrates superiority through perfect predictions",
        "Exploits overconfidence and emotional reactions"
      ],
      warning: "Master-level difficulty. Designed to test both skill and emotional stability.",
      isLocked: playerStats?.totalGems < 1500 || playerStats?.bestStreak < 20,
      isNew: true,
      unlockRequirements: [
        { type: 'gems', label: 'Gems', icon: 'Gem', current: playerStats?.totalGems, required: 1500 },
        { type: 'streak', label: 'Streak', icon: 'Flame', current: playerStats?.bestStreak, required: 20 },
      ],
    },
  ];

  const [selectedMoodId, setSelectedMoodId] = useState(1);
  const [previewMood, setPreviewMood] = useState(null);
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [showProgressionTracker, setShowProgressionTracker] = useState(false);

  const difficultyOptions = [
    { value: 'all', label: 'All Difficulties' },
    { value: 'Easy', label: 'Easy' },
    { value: 'Medium', label: 'Medium' },
    { value: 'Hard', label: 'Hard' },
    { value: 'Extreme', label: 'Extreme' },
  ];

  const filteredMoods = filterDifficulty === 'all' 
    ? aiMoods 
    : aiMoods?.filter(mood => mood?.difficulty === filterDifficulty);

  const handleMoodSelect = (moodId) => {
    const mood = aiMoods?.find(m => m?.id === moodId);
    if (!mood?.isLocked) {
      setSelectedMoodId(moodId);
    }
  };

  const handlePreview = (mood) => {
    setPreviewMood(mood);
  };

  const handleStartGame = () => {
    const selectedMood = aiMoods?.find(m => m?.id === selectedMoodId);
    if (selectedMood && !selectedMood?.isLocked) {
      selectAIMood(selectedMood);
      navigate('/game-arena', { state: { selectedMood } });
    }
  };

  const handleReturnToMenu = () => {
    navigate('/main-menu');
  };

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      <GameFlowNavigation />
      <main className="pt-20 pb-24 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4 md:mb-6">
              <div>
                <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-primary mb-2 neon-text-cyan">
                  Select AI Mood
                </h1>
                <p className="text-sm md:text-base text-muted-foreground font-caption">
                  Choose your psychological opponent and prepare for battle
                </p>
              </div>
              <Button
                variant="outline"
                size="default"
                iconName={showProgressionTracker ? "ChevronUp" : "Award"}
                iconPosition="left"
                onClick={() => setShowProgressionTracker(!showProgressionTracker)}
                className="lg:flex-shrink-0"
              >
                {showProgressionTracker ? 'Hide' : 'Show'} Progress
              </Button>
            </div>

            {showProgressionTracker && (
              <div className="mb-6 md:mb-8">
                <ProgressionTracker stats={playerStats} />
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 p-4 md:p-6 bg-card border-2 border-primary/50 rounded-sm shadow-glow-cyan-sm">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0 bg-primary/20 rounded-sm flex items-center justify-center border border-primary">
                  <Icon name="Filter" size={24} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <Select
                    options={difficultyOptions}
                    value={filterDifficulty}
                    onChange={setFilterDifficulty}
                    placeholder="Filter by difficulty"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm md:text-base font-caption text-muted-foreground">
                <Icon name="Info" size={18} color="var(--color-primary)" />
                <span>
                  {filteredMoods?.length} mood{filteredMoods?.length !== 1 ? 's' : ''} available
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            {filteredMoods?.map((mood) => (
              <MoodCard
                key={mood?.id}
                mood={mood}
                isSelected={selectedMoodId === mood?.id}
                onSelect={handleMoodSelect}
                onPreview={handlePreview}
              />
            ))}
          </div>

          {filteredMoods?.length === 0 && (
            <div className="text-center py-12 md:py-16">
              <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 bg-muted/20 rounded-sm flex items-center justify-center border border-muted">
                <Icon name="Search" size={40} color="var(--color-muted-foreground)" />
              </div>
              <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-2">
                No Moods Found
              </h3>
              <p className="text-sm md:text-base text-muted-foreground font-caption mb-4">
                Try adjusting your difficulty filter
              </p>
              <Button
                variant="outline"
                size="default"
                onClick={() => setFilterDifficulty('all')}
              >
                Clear Filter
              </Button>
            </div>
          )}

          <div className="sticky bottom-4 md:bottom-6 bg-card border-2 border-primary rounded-sm p-4 md:p-6 shadow-glow-cyan-lg">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="Check" size={18} color="var(--color-accent)" />
                  <span className="text-sm md:text-base font-caption text-muted-foreground">
                    Selected Mood:
                  </span>
                </div>
                <p className="text-lg md:text-xl font-heading font-bold text-primary">
                  {aiMoods?.find(m => m?.id === selectedMoodId)?.name || 'None'}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={handleReturnToMenu}
                  fullWidth
                >
                  Back to Menu
                </Button>
                <Button
                  variant="default"
                  size="lg"
                  iconName="Play"
                  iconPosition="right"
                  onClick={handleStartGame}
                  disabled={!selectedMoodId || aiMoods?.find(m => m?.id === selectedMoodId)?.isLocked}
                  fullWidth
                >
                  Start Game
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      {previewMood && (
        <PreviewModal
          mood={previewMood}
          onClose={() => setPreviewMood(null)}
          onSelect={handleMoodSelect}
        />
      )}
    </div>
  );
};

export default AIMoodSelection;